import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db,storage } from "../../../firebase";
import { Send } from "lucide-react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const backendUrl = process.env.REACT_APP_BACKEND_URL;
;


const uploadToFirebase = async (file) => {
  if (!file) throw new Error("No se seleccionó ningún archivo.");

  try {
    // Crear ruta única en Firebase Storage
    const filePath = `mediaManagamentPromotionalAccounts/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filePath);

    // Subir archivo
    const snapshot = await uploadBytes(storageRef, file);

    // Obtener URL pública
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Determinar tipo de recurso y formato
    const extension = file.name.split(".").pop().toLowerCase();
    const rawExtensions = ["pdf", "doc", "docx", "xls", "xlsx", "txt"];
    const resource_type = rawExtensions.includes(extension) ? "raw" : file.type.startsWith("image") ? "image" : file.type.startsWith("video") ? "video" : "file";

    const format = extension;

    return {
      url: downloadURL,
      format,
      resource_type,
    };
  } catch (error) {
    throw new Error(`Error al subir el archivo a Firebase Storage: ${error.message}`);
  }
};

const ViewMessages = ({ chatId, promoProfileUid,otherUserUid, otherUserData }) => {
  const currentUserUid = auth.currentUser?.uid;
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const otherUser = {
    uid: otherUserUid,
    isPromo: otherUserData?.isPromo,
    ownerUid: otherUserData?.ownerUid,
  };

  useEffect(() => {
    if (!chatId || !currentUserUid) {
      setError("Chat ID o usuario no válido.");
      return;
    }

    const messagesRef = collection(db, "chatsPromotional", chatId, "messages");
    const q = query(messagesRef, orderBy("createdAt", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const msgs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMessages(msgs);
        setError(null);

        // Marcar mensajes no leídos como leídos
        const unreadMessages = msgs.filter(
          (msg) => msg.sender !== currentUserUid && !msg.read
        );
        if (unreadMessages.length > 0) {
          Promise.all(
            unreadMessages.map((msg) =>
              updateDoc(doc(db, "chatsPromotional", chatId, "messages", msg.id), {
                read: true,
              })
            )
          ).catch((error) => {
            console.error("Error marcando mensajes como leídos:", error);
            setError("Error al actualizar el estado de los mensajes.");
          });
        }

        // Actualizar estado de lectura en el chat
        updateDoc(doc(db, "chatsPromotional", chatId), {
          [`readStatus.${currentUserUid}`]: true,
        }).catch((error) => {
          console.error("Error actualizando readStatus:", error);
          setError("Error al actualizar el estado del chat.");
        });
      },
      (error) => {
        console.error("Error en onSnapshot:", error);
        setError("Error al cargar los mensajes. Inténtalo de nuevo.");
      }
    );

    return () => unsubscribe();
  }, [chatId, currentUserUid]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

const handleSend = async (e) => {
  e.preventDefault();

  if (!newMessage.trim() && !file) return;
  if (!chatId || !currentUserUid) {
    setError("Chat ID o usuario no válido.");
    return;
  }

  setUploading(true);

  try {
    let attachments = [];

    // --- Subir archivo a Storage si existe ---
    if (file) {
      const uploaded = await uploadToFirebase(file);
      attachments.push({
        url: uploaded.url,
        format: uploaded.format,
        resource_type: uploaded.resource_type,
      });
    }

    // --- Guardar mensaje en Firestore ---
    const messagesRef = collection(db, "chatsPromotional", chatId, "messages");
    await addDoc(messagesRef, {
      text: newMessage.trim() || "",
      sender: currentUserUid,
      createdAt: serverTimestamp(),
      read: false,
      attachments,
    });

    // --- Actualizar documento del chat ---
    const chatDocRef = doc(db, "chatsPromotional", chatId);
    await updateDoc(chatDocRef, {
      lastMessage: newMessage.trim() || (file ? "Archivo adjunto" : ""),
      lastMessageTimestamp: serverTimestamp(),
      [`readStatus.${currentUserUid}`]: true,
      [`readStatus.${promoProfileUid}`]: false, // El otro usuario aún no leyó
    });

    // --- Enviar notificación push vía backend ---
    try {
      const toUid =
        otherUser?.isPromo && otherUser?.ownerUid
          ? otherUser.ownerUid
          : otherUser?.uid;

      const isProm =
        otherUserData?.role === "promocional" ||
        otherUserData?.carrera === "Promocional";

      const endpoint = isProm
        ? `${backendUrl}/api/send-prom-notification`
        : `${backendUrl}/api/send-notification`;

      const idToken = await auth.currentUser.getIdToken();
      await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          to: toUid,
          from: currentUserUid,
          message: newMessage.trim(),
          chatId,
        }),
      });
    } catch (err) {
      console.error("Error sending push notification:", err);
    }

    // --- Limpiar input ---
    setNewMessage("");
    setFile(null);
    setError(null);
  } catch (error) {
    console.error("Error enviando mensaje:", error);
    setError(`Error al enviar el mensaje: ${error.message}`);
  } finally {
    setUploading(false);
  }
};


  if (!currentUserUid) {
    return (
      <p className="text-red-500 text-center p-4 bg-red-100 dark:bg-red-900/30 rounded-lg">
        Debes estar autenticado para ver los mensajes.
      </p>
    );
  }

  if (!chatId) {
    return (
      <p className="text-gray-600 text-center p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        Selecciona un chat para ver los mensajes.
      </p>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-[80vh] w-full max-w-4xl mx-auto bg-white dark:bg-gray-900">
      {error && (
        <p className="text-red-500 text-center p-4 bg-red-100 dark:bg-red-900/30 rounded-lg">
          {error}
        </p>
      )}

      <div
        className="flex-grow overflow-y-auto px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-800 shadow-inner border border-gray-300 dark:border-gray-700 space-y-3 max-h-[60vh] sm:max-h-[70vh]"
      >
        {messages.length === 0 ? (
          <p className="text-gray-500 text-center mt-8">No hay mensajes todavía.</p>
        ) : (
          messages.map((msg) => {
            const isCurrentUser = msg.sender === currentUserUid;
            return (
              <div
                key={msg.id}
                className={`flex ${isCurrentUser ? "justify-end" : "justify-start"} w-full`}
              >
                <div
                  className={`max-w-[70%] px-4 py-2 rounded-2xl shadow-md break-words text-sm ${
                    isCurrentUser
                      ? "bg-indigo-600 text-white rounded-br-sm"
                      : "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-sm"
                  }`}
                >
                  {msg.text && <p>{msg.text}</p>}

                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {msg.attachments.map((att, i) => {
                        // Ensure resource_type is handled correctly
                        if (att.resource_type === "image") {
                          return (
                            <img
                              key={i}
                              src={att.url}
                              alt="Adjunto"
                              className="max-w-full rounded-lg h-auto"
                            />
                          );
                        }
                        if (att.resource_type === "video") {
                          return (
                            <video
                              key={i}
                              controls
                              src={att.url}
                              className="max-w-full rounded-lg h-auto"
                            />
                          );
                        }
                        if (att.resource_type === "raw") {
                          return (
                            <a
                              key={i}
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-800 dark:hover:text-indigo-300"
                            >
                              {att.format === "pdf"
                                ? "Ver/Descargar PDF"
                                : `Ver/Descargar documento (${att.format || "archivo"})`}
                            </a>
                          );
                        }
                        // Fallback for unexpected resource_type
                        return (
                          <a
                            key={i}
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-800 dark:hover:text-indigo-300"
                          >
                            Ver/Descargar archivo ({att.format || "desconocido"})
                          </a>
                        );
                      })}
                    </div>
                  )}

                  {msg.createdAt?.seconds && (
                    <small className="block text-xs text-right mt-1 opacity-60">
                      {new Date(msg.createdAt.seconds * 1000).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </small>
                  )}

                  {isCurrentUser && (
                    <span className="flex items-center justify-end">
                      {msg.read ? (
                        <span className="text-xs text-white opacity-90">Leído</span>
                      ) : (
                        <span className="text-xs text-white opacity-70">Enviado</span>
                      )}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="w-full max-w-full px-2 sm:px-4 mt-4">
      {file && (
        <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 rounded-full px-3 py-1 mb-2 text-sm text-gray-700 dark:text-gray-200">
          <span className="truncate">{file.name}</span>
          <button
            onClick={() => setFile(null)}
            className="ml-2 text-red-500 hover:underline flex-shrink-0"
            type="button"
            aria-label="Eliminar archivo"
          >
            ✖
          </button>
        </div>
      )}

      <form
        onSubmit={handleSend}
        className="flex items-center gap-2 w-full max-w-full flex-wrap sm:flex-nowrap"
      >
        <input
          type="text"
          placeholder="Escribe un mensaje..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          className="flex-grow min-w-[150px] px-4 py-2 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm sm:text-base"
          disabled={uploading}
        />

        <label
          htmlFor="file-upload"
          className="cursor-pointer p-2 rounded-full bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 transition flex-shrink-0"
          title="Adjuntar archivo"
        >
          📎
        </label>
        <input
          id="file-upload"
          type="file"
          accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          onChange={handleFileChange}
          className="hidden"
          disabled={uploading}
        />

        <button
          type="submit"
          disabled={uploading || (!newMessage.trim() && !file)}
          className="shrink-0 p-2 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-400 transition"
          aria-label="Enviar mensaje"
        >
          <Send size={20} />
        </button>
      </form>
    </div>
    </div>
  );
};

export default ViewMessages;